import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Badge,
  Input,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  useDisclosure,
  useToast,
  Spinner,
  Switch,
} from '@chakra-ui/react';
import { PencilSimpleLine, Trash, Plus } from 'phosphor-react';
import { PageHeader } from '../../../components/PageHeader';
import { TableBox } from '../../../components/tableBox';
import { EmptyData } from '../../../components/emptyData';
import { ActionsButton } from '../../../components/buttons/ActionsButton';
import { useDialog } from '../../../providers/dialog';
import { api } from '../../../services/api';
import { colors } from '../../../theme/colors';

interface Motivo {
  id: number;
  descricao: string;
  tela: string;
  ativo: boolean;
}

const TELAS_DISPONIVEIS = [
  { value: 'empresas', label: 'Empresas' },
  { value: 'faturamento', label: 'Faturamento' },
  { value: 'simples_nacional', label: 'Simples Nacional' },
  { value: 'fiscal', label: 'Fiscal' },
  { value: 'fgts_folha', label: 'Folha/FGTS' },
  { value: 'api_sieg', label: 'API Sieg' },
  { value: 'quebra_sequencia', label: 'Quebra Sequência' },
  { value: 'nfseDashboard', label: 'NFS-e' },
];

export default function MotivosAnotacaoPage() {
  const toast = useToast();
  const showDialog = useDialog();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [motivos, setMotivos] = useState<Motivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [telaFiltro, setTelaFiltro] = useState('fiscal');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formDescricao, setFormDescricao] = useState('');
  const [formTela, setFormTela] = useState('fiscal');
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const fetchMotivos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v1/anotacao-motivos/', {
        params: { tela: telaFiltro, apenas_ativos: false },
      });
      const data = res.data?.data || res.data;
      setMotivos(Array.isArray(data) ? data : []);
    } catch {
      setMotivos([]);
      toast({ title: 'Erro ao carregar motivos', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [telaFiltro]);

  useEffect(() => {
    fetchMotivos();
  }, [fetchMotivos]);

  const openCreate = () => {
    setEditingId(null);
    setFormDescricao('');
    setFormTela(telaFiltro);
    onOpen();
  };

  const openEdit = (motivo: Motivo) => {
    setEditingId(motivo.id);
    setFormDescricao(motivo.descricao);
    setFormTela(motivo.tela);
    onOpen();
  };

  const handleSave = async () => {
    if (!formDescricao.trim()) {
      toast({ title: 'Descrição é obrigatória', status: 'warning', duration: 3000 });
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/api/v1/anotacao-motivos/${editingId}/`, {
          descricao: formDescricao.trim(),
        });
        toast({ title: 'Motivo atualizado', status: 'success', duration: 3000 });
      } else {
        await api.post('/api/v1/anotacao-motivos/', {
          descricao: formDescricao.trim(),
          tela: formTela,
        });
        toast({ title: 'Motivo criado', status: 'success', duration: 3000 });
      }
      onClose();
      await fetchMotivos();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast({
        title: typeof detail === 'string' ? detail : 'Erro ao salvar',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (motivo: Motivo) => {
    showDialog({
      status: 'warning',
      title: `Remover "${motivo.descricao}"?`,
      description:
        'O motivo será removido permanentemente e não aparecerá mais na lista de opções.',
      cancelLabel: 'Cancelar',
      async onConfirm() {
        try {
          await api.delete(`/api/v1/anotacao-motivos/${motivo.id}/`);
          toast({ title: 'Motivo removido', status: 'success', duration: 3000 });
          await fetchMotivos();
        } catch {
          toast({ title: 'Erro ao remover', status: 'error', duration: 3000 });
        }
      },
    });
  };

  const handleToggleAtivo = async (motivo: Motivo) => {
    if (togglingId === motivo.id) return;
    setTogglingId(motivo.id);
    // Optimistic update
    setMotivos(prev => prev.map(m => (m.id === motivo.id ? { ...m, ativo: !m.ativo } : m)));
    try {
      await api.put(`/api/v1/anotacao-motivos/${motivo.id}/`, {
        ativo: !motivo.ativo,
      });
    } catch {
      // Reverter optimistic update
      setMotivos(prev => prev.map(m => (m.id === motivo.id ? { ...m, ativo: motivo.ativo } : m)));
      toast({ title: 'Erro ao atualizar', status: 'error', duration: 3000 });
    } finally {
      setTogglingId(null);
    }
  };

  const telaLabel = (tela: string) => TELAS_DISPONIVEIS.find(t => t.value === tela)?.label || tela;

  return (
    <Box>
      <PageHeader
        title="Motivos de Anotação"
        subtitle="Gerencie as opções de motivos disponíveis ao criar anotações nas telas de auditoria."
      />

      <TableBox>
        <Flex justify="space-between" align="center" mb={4}>
          <Flex align="center" gap={3}>
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Tela:
            </Text>
            <Select
              size="sm"
              w="200px"
              borderRadius="lg"
              value={telaFiltro}
              onChange={e => setTelaFiltro(e.target.value)}
            >
              {TELAS_DISPONIVEIS.map(t => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Select>
          </Flex>
          <Button
            size="sm"
            colorScheme="green"
            leftIcon={<Plus size={14} weight="bold" />}
            onClick={openCreate}
          >
            Novo Motivo
          </Button>
        </Flex>

        <div className="border border-b-divider" />

        {loading && (
          <Flex justify="center" py={10}>
            <Spinner size="lg" color={colors.primary} />
          </Flex>
        )}

        {!loading && motivos.length === 0 && (
          <EmptyData
            title="Nenhum motivo cadastrado"
            subtitle={`Cadastre motivos para a tela "${telaLabel(telaFiltro)}"`}
            buttonLabel="Cadastrar motivo"
            onClick={openCreate}
            icon={<Plus size={90} color={colors.primary} />}
          />
        )}

        {!loading && motivos.length > 0 && (
          <Box>
            <Flex
              px={4}
              py={3}
              bg="gray.50"
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="semibold"
              fontSize="xs"
              color="gray.500"
              textTransform="uppercase"
            >
              <Text flex={1}>Descrição</Text>
              <Text w="120px" textAlign="center">
                Tela
              </Text>
              <Text w="80px" textAlign="center">
                Ativo
              </Text>
              <Text w="80px" textAlign="center">
                Ações
              </Text>
            </Flex>
            {motivos.map(motivo => (
              <Flex
                key={motivo.id}
                px={4}
                py={3}
                align="center"
                borderBottom="1px solid"
                borderColor="gray.100"
                _hover={{ bg: 'gray.50' }}
              >
                <Text flex={1} fontSize="sm">
                  {motivo.descricao}
                </Text>
                <Flex w="120px" justify="center">
                  <Badge colorScheme="blue" fontSize="2xs" borderRadius="full" px={2}>
                    {telaLabel(motivo.tela)}
                  </Badge>
                </Flex>
                <Flex w="80px" justify="center">
                  <Switch
                    size="sm"
                    isChecked={motivo.ativo}
                    isDisabled={togglingId === motivo.id}
                    onChange={() => handleToggleAtivo(motivo)}
                    colorScheme="green"
                  />
                </Flex>
                <Flex w="80px" justify="center">
                  <ActionsButton aria-label="actions">
                    <Button
                      variant="unstyled"
                      p="6px"
                      display="flex"
                      alignItems="center"
                      gap={2}
                      onClick={() => openEdit(motivo)}
                    >
                      <PencilSimpleLine size={16} weight="bold" />
                      <Text fontSize="sm">Editar</Text>
                    </Button>
                    <Button
                      variant="unstyled"
                      p="6px"
                      display="flex"
                      alignItems="center"
                      gap={2}
                      onClick={() => handleDelete(motivo)}
                    >
                      <Trash size={16} weight="bold" />
                      <Text fontSize="sm">Remover</Text>
                    </Button>
                  </ActionsButton>
                </Flex>
              </Flex>
            ))}
          </Box>
        )}
      </TableBox>

      {/* Modal Criar/Editar */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingId ? 'Editar Motivo' : 'Novo Motivo'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" flexDirection="column" gap={4}>
            <FormControl isRequired>
              <FormLabel>Descrição</FormLabel>
              <Input
                value={formDescricao}
                onChange={e => setFormDescricao(e.target.value)}
                placeholder="Ex: Empresa com retenções federais"
              />
            </FormControl>

            {!editingId && (
              <FormControl isRequired>
                <FormLabel>Tela</FormLabel>
                <Select value={formTela} onChange={e => setFormTela(e.target.value)}>
                  {TELAS_DISPONIVEIS.map(t => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="green" onClick={handleSave} isLoading={saving}>
              {editingId ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
